// src/Pages/Owner/OwnerEditInfoPage/OwnerEditInfoPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ownerAPI from '../../../services/ownerApi';
import { useSelector, useDispatch } from 'react-redux';
import OwnerSidebar from '../../../Components/OwnerSidebar/OwnerSidebar';
import { fetchOwnerProfile } from '../../../store/slices/ownerAuthSlice';
import './OwnerEditInfoPage.css';
import { ReactSortable } from 'react-sortablejs';
import ClubCard from '../../../Components/ClubCard/ClubCard';
import { Switch } from '@headlessui/react';
import { timeManager } from '../../../utils/timeUtils';

const GENRE_LIST = [
  'edm', 'hiphop', 'techno', 'pop',
  'funk', 'disco', 'house', 'chill',
  'party', 'latino', 'kpop', 'r&b',
  'rock', 'retro', 'indie', 'emo',
  'all kinds', 'trance'
];

const initialWeeklyHours = [
  { day: 'mon', label: '월요일', start: '', end: '', closed: false, isLate: false },
  { day: 'tue', label: '화요일', start: '', end: '', closed: false, isLate: false },
  { day: 'wed', label: '수요일', start: '', end: '', closed: false, isLate: false },
  { day: 'thu', label: '목요일', start: '', end: '', closed: false, isLate: false },
  { day: 'fri', label: '금요일', start: '', end: '', closed: false, isLate: false },
  { day: 'sat', label: '토요일', start: '', end: '', closed: false, isLate: false },
  { day: 'sun', label: '일요일', start: '', end: '', closed: false, isLate: false },
];

// 기존 레거시 함수들 (호환성 유지)
const parseOperatingHours = (hoursString) => {
  const parsedHours = [...initialWeeklyHours.map(day => ({...day}))];
  if (!hoursString) return parsedHours;

  const lines = hoursString.split('\n');
  lines.forEach(line => {
    const parts = line.split(':');
    if (parts.length < 2) return;
    const dayLabel = parts[0].trim();
    const timePart = parts.slice(1).join(':').trim();

    const dayIndex = parsedHours.findIndex(d => d.label === dayLabel);
    if (dayIndex === -1) return;

    if (timePart === '휴무') {
      parsedHours[dayIndex].closed = true;
      parsedHours[dayIndex].start = '';
      parsedHours[dayIndex].end = '';
    } else {
      const timeMatch = timePart.match(/(\d{2}:\d{2})\s*~\s*(\d{2}:\d{2}|late|early|midnight|noon)/);
      if (timeMatch) {
        parsedHours[dayIndex].start = timeMatch[1];
        const endTime = timeMatch[2];
        if (endTime === 'late' || endTime === 'early' || endTime === 'midnight' || endTime === 'noon') {
          parsedHours[dayIndex].end = '';
          parsedHours[dayIndex].isLate = true;
        } else {
          parsedHours[dayIndex].end = endTime;
          parsedHours[dayIndex].isLate = false;
        }
        parsedHours[dayIndex].closed = false;
      } else {
        parsedHours[dayIndex].start = '';
        parsedHours[dayIndex].end = '';
        parsedHours[dayIndex].isLate = false;
        parsedHours[dayIndex].closed = false;
      }
    }
  });
  return parsedHours;
};

const formatOperatingHours = (weeklyHoursArray) => {
  return weeklyHoursArray.map(day => {
    if (day.closed) {
      return `${day.label}: 휴무`;
    } else if (day.start && (day.end || day.isLate)) {
      // isLate가 true이면 "late"로 표시, 아니면 실제 end 시간 사용
      const endTime = day.isLate ? 'late' : day.end;
      return `${day.label}: ${day.start} ~ ${endTime}`;
    } else if (day.start && !day.end && !day.isLate) {
      // 시작시간은 있지만 끝나는시간이 없고 late도 아닌 경우 자동으로 late 처리
      return `${day.label}: ${day.start} ~ late`;
    } else {
      return null;
    }
  }).filter(line => line !== null).join('\n');
};

// 새로운 압축 포맷 관련 함수들
const formatWeeklyHoursToCompact = (weeklyHoursArray) => {
  // timeManager 사용하여 압축 포맷 생성
  const detailedString = formatOperatingHours(weeklyHoursArray);
  const scheduleJSON = timeManager.parseStringToJSON(detailedString);
  return timeManager.formatJSONToCompact(scheduleJSON);
};

const parseCompactToWeeklyHours = (compactString) => {
  // 압축 포맷을 기존 weekly hours 배열로 변환
  if (!compactString) return initialWeeklyHours;
  
  // timeManager를 사용하여 다양한 포맷 파싱 시도
  let scheduleJSON = null;
  
  // 1. 압축 포맷 파싱 시도
  try {
    scheduleJSON = timeManager.parseCompactToJSON(compactString);
  } catch (e) {
    console.warn('압축 포맷 파싱 실패:', e);
  }
  
  // 2. 기존 상세 포맷 파싱 시도
  if (!scheduleJSON) {
    try {
      scheduleJSON = timeManager.parseStringToJSON(compactString);
    } catch (e) {
      console.warn('상세 포맷 파싱 실패:', e);
    }
  }
  
  // 3. 파싱 실패한 경우 기본값 반환
  if (!scheduleJSON || !scheduleJSON.schedule) {
    console.warn('운영시간 파싱 실패, 기본값 사용:', compactString);
    return initialWeeklyHours;
  }
  
  const weeklyHours = [...initialWeeklyHours.map(day => ({...day}))];
  
  scheduleJSON.schedule.forEach(item => {
    item.days.forEach(dayKey => {
      const dayIndex = weeklyHours.findIndex(d => d.day === dayKey);
      if (dayIndex !== -1) {
        if (item.hours) {
          weeklyHours[dayIndex].start = item.hours.start;
          if (item.hours.end === 'late') {
            weeklyHours[dayIndex].end = '';
            weeklyHours[dayIndex].isLate = true;
          } else {
            weeklyHours[dayIndex].end = item.hours.end;
            weeklyHours[dayIndex].isLate = false;
          }
          weeklyHours[dayIndex].closed = false;
        } else {
          weeklyHours[dayIndex].start = '';
          weeklyHours[dayIndex].end = '';
          weeklyHours[dayIndex].isLate = false;
          weeklyHours[dayIndex].closed = true;
        }
      }
    });
  });
  
  return weeklyHours;
};

const parsePrice = (priceString) => {
    const prices = { men: '', women: '' };
    if (!priceString) return prices;

    const parts = priceString.split(',');
    parts.forEach(part => {
        const trimmedPart = part.trim();
        if (trimmedPart.startsWith('남자:')) {
            prices.men = trimmedPart.replace(/[^0-9]/g, '');
        } else if (trimmedPart.startsWith('여자:')) {
            prices.women = trimmedPart.replace(/[^0-9]/g, '');
        }
    });
    return prices;
};

// 개인 연락처 정보 필터링 함수 추가
const containsContactInfo = (text) => {
  if (!text) return [false, []];
  
  // 텍스트를 단어 또는 구문 단위로 분리
  // 줄바꿈, 공백, 구두점 등으로 구분
  const words = text.split(/[\s,.!?:;()[\]{}'"]+/).filter(word => word);
  
  // 정규식 패턴들
  const phoneRegex = /^01[016789][- ]?\d{3,4}[- ]?\d{4}$/;
  const kakaoRegex = /^(?:카카오|카톡|kakao|KakaoTalk)\s*(?:ID|아이디|id)?$/i;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const urlRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

  // how2play.kr 존재 여부 확인
  const hasHow2play = words.some(word => word.includes('how2play.kr'));
  
  // how2play.kr이 포함된 경우 모든 연락처 정보 허용
  if (hasHow2play) {
    return [false, []];
  }
  
  // 위반 사항 초기화
  const violations = [];
  let hasPhone = false;
  let hasKakao = false;
  let hasEmail = false;
  let hasBlockedUrl = false;
  
  // 단어별 검사
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    
    // 전화번호 패턴 (한국 휴대폰)
    if (phoneRegex.test(word) || /^01[016789]\d{7,8}$/.test(word)) {
      hasPhone = true;
    }
    
    // 카카오톡 ID 패턴
    // ID:xxx 같은 형태도 검사
    if (kakaoRegex.test(word) && i + 1 < words.length) {
      hasKakao = true;
    }
    
    // 카톡 ID가 한 단어에 붙어있는 경우 (예: "카카오ID:abcd")
    if (/(?:카카오|카톡|kakao|kakaotalk)(?:ID|아이디|id)?[:\s]?\w+/i.test(word)) {
      hasKakao = true;
    }
    
    // 이메일 패턴
    if (emailRegex.test(word)) {
      hasEmail = true;
    }
    
    // URL 패턴 (how2play.kr 제외)
    if (urlRegex.test(word) && !word.includes('how2play.kr')) {
      hasBlockedUrl = true;
    }
  }
  
  // 전체 텍스트에서 패턴 검사 (단어 분리로 놓칠 수 있는 경우를 위해)
  // 전화번호는 숫자 사이에 하이픈이나 공백이 있을 수 있음
  if (!hasPhone) {
    hasPhone = /01[016789][- ]?\d{3,4}[- ]?\d{4}/.test(text);
  }
  
  // 위반사항 기록
  if (hasPhone && violations) violations.push('전화번호');
  if (hasKakao && violations) violations.push('카카오톡 ID');
  if (hasEmail && violations) violations.push('이메일');
  if (hasBlockedUrl && violations) violations.push('외부 웹사이트 URL');
  
  return [violations.length > 0, violations];
};

export default function OwnerEditInfoPage() {
  const dispatch = useDispatch();
  const { owner, isAuthenticated } = useSelector((state) => state.ownerAuth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [club, setClub] = useState(null);
  const [clubs, setClubs] = useState([]); // 슈퍼유저용 클럽 목록
  const [selectedClubId, setSelectedClubId] = useState(null); // 선택된 클럽 ID
  const [formData, setFormData] = useState({
    name: '',
    korean_name: '',
    address: '',
    description: '',
    playlist: '',
    genres: '',
    smoking_policy: '금연',
  });
  const [weeklyHours, setWeeklyHours] = useState(initialWeeklyHours);
  
  // 새로운 운영시간 입력 모드 상태
  const [operatingMode, setOperatingMode] = useState('detailed'); // 'detailed' or 'compact'
  const [compactHours, setCompactHours] = useState(''); // 압축 포맷 문자열
  
  const [priceMen, setPriceMen] = useState('');
  const [priceWomen, setPriceWomen] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [mainImage, setMainImage] = useState('');
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [error, setError] = useState('');
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    const previews = (newImages || []).map(file => URL.createObjectURL(file));
    setNewImagePreviews(previews);
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [newImages]);

  // 오너 프로필 가져오기
  useEffect(() => {
    if (isAuthenticated && !owner) {
      dispatch(fetchOwnerProfile());
    }
  }, [dispatch, isAuthenticated, owner]);

  // 슈퍼유저의 경우 클럽 목록 가져오기
  useEffect(() => {
    if (owner?.is_superuser) {
      fetchClubsList();
    }
  }, [owner]);

  // URL 파라미터에서 clubId 확인 및 즉시 로딩
  useEffect(() => {
    const clubIdParam = searchParams.get('clubId');
    if (clubIdParam && owner?.is_superuser) {
      const clubId = parseInt(clubIdParam);
      setSelectedClubId(clubId);
      fetchClubById(clubId);
    }
  }, [searchParams, owner]);

  // 슈퍼유저 클럽 선택 변경 시 클럽 정보 로딩
  useEffect(() => {
    if (selectedClubId && owner?.is_superuser) {
      fetchClubById(selectedClubId);
    }
  }, [selectedClubId, owner]);

  const fetchClubsList = async () => {
    try {
      const clubsList = await ownerAPI.getClubs();
      setClubs(clubsList);
      
      // URL 파라미터가 없고 클럽이 있으면 첫 번째 클럽 선택
      if (!searchParams.get('clubId') && clubsList.length > 0) {
        const firstClubId = clubsList[0].id;
        setSelectedClubId(firstClubId);
        fetchClubById(firstClubId);
      }
    } catch (err) {
      console.error('❌ Failed to fetch clubs list:', err);
      setError('클럽 목록을 불러오지 못했습니다.');
    }
  };

  const fetchClubById = async (clubId) => {
    const token = localStorage.getItem('ownerToken');
    if (!token) {
      navigate('/owner/login');
      return;
    }

    try {
      // 클럽 변경 시 이전 클럽의 새 이미지 상태 초기화
      setNewImages([]);
      setNewImagePreviews([]);
      
      // 직접 API를 호출하여 클럽 정보 가져오기
      const clubData = await ownerAPI.getClubById(clubId);
      setClub(clubData);
      populateFormData(clubData);
    } catch (err) {
      console.error('❌ Failed to fetch club:', err);
      setError('클럽 정보를 불러오지 못했습니다.');
    }
  };

  const populateFormData = (clubData) => {
    setFormData({
      name: clubData.name || '',
      korean_name: clubData.korean_name || '',
      address: clubData.address || '',
      description: clubData.description || '',
      playlist: clubData.playlist || '',
      genres: clubData.genres || '',
      smoking_policy: clubData.smoking_policy || '금연',
    });

    // 운영시간 설정 - 다양한 포맷 감지
    if (clubData.operating_hours) {
      // timeManager로 포맷 감지 및 파싱
      const operatingHours = clubData.operating_hours.trim();
      
      // 기존 상세 포맷인지 확인 (예: "월요일: 22:00 ~ 06:00")
      const isDetailedFormat = operatingHours.includes('요일:') && operatingHours.includes('~');
      
      // 새로운 압축 포맷이거나 영어 포맷인지 확인
      const compactFormatJSON = timeManager.parseCompactToJSON(operatingHours);
      const englishFormatJSON = timeManager.parseEnglishOperatingLine && 
                               timeManager.parseEnglishOperatingLine(operatingHours) ? 
                               timeManager.parseCompactToJSON(operatingHours) : null;
      
      if (isDetailedFormat) {
        // 기존 상세 포맷
        setOperatingMode('detailed');
        setWeeklyHours(parseOperatingHours(clubData.operating_hours));
        setCompactHours(formatWeeklyHoursToCompact(parseOperatingHours(clubData.operating_hours)));
      } else if (compactFormatJSON || englishFormatJSON) {
        // 새로운 압축 포맷 또는 영어 포맷
        setOperatingMode('compact');
        setCompactHours(clubData.operating_hours);
        
        // timeManager를 사용하여 파싱
        let scheduleJSON = compactFormatJSON || englishFormatJSON;
        if (!scheduleJSON) {
          // 직접 파싱 시도
          scheduleJSON = timeManager.parseStringToJSON(clubData.operating_hours);
        }
        
        if (scheduleJSON) {
          setWeeklyHours(parseCompactToWeeklyHours(clubData.operating_hours));
        } else {
          // 파싱 실패한 경우 기본값 사용
          setWeeklyHours(initialWeeklyHours);
        }
      } else {
        // 알 수 없는 포맷 - 문자열 그대로 압축 모드로 표시
        setOperatingMode('compact');
        setCompactHours(clubData.operating_hours);
        setWeeklyHours(initialWeeklyHours);
      }
    } else {
      setWeeklyHours(initialWeeklyHours);
      setCompactHours('');
    }

    const prices = parsePrice(clubData.price);
    setPriceMen(prices.men);
    setPriceWomen(prices.women);

    setExistingImages(clubData.images || []);
    if (clubData.images && clubData.images.length > 0) {
      setMainImage(clubData.images[0].image_url);
    } else {
      setMainImage('');
    }
  };

  useEffect(() => {
    if (existingImages.length > 0 && existingImages[0]?.image_url) {
       setMainImage(existingImages[0].image_url);
    } else if (newImagePreviews.length > 0) {
       setMainImage(newImagePreviews[0]);
    } else {
       setMainImage('');
    }
  }, [existingImages, newImagePreviews]);

  // 일반 오너의 경우에만 클럽 정보 가져오기
  useEffect(() => {
    if (owner && !owner.is_superuser) {
      fetchMyClub();
    }
  }, [owner, navigate]);

  const fetchMyClub = async () => {
    const token = localStorage.getItem('ownerToken');
    if (!token) {
      navigate('/owner/login');
      return;
    }
    try {
      const clubData = await ownerAPI.getMyClub();
      setClub(clubData);
      populateFormData(clubData);
    } catch (err) {
      console.error('❌ My Club fetch failed:', err);
      setError('클럽 정보를 불러오지 못했습니다.');
      if (err.response?.status === 401) {
        navigate('/owner/login');
      }
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // description 필드 변경 시 연락처 정보 검사
    if (name === 'description') {
      const [hasContactInfo, violations] = containsContactInfo(value);
      if (hasContactInfo) {
        setContactError(`개인 연락처 정보는 입력할 수 없습니다. 감지된 항목: ${violations.join(', ')}`);
      } else {
        setContactError('');
      }
    }
  };

  const handleWeeklyHoursChange = (day, field, value) => {
    const updatedHours = weeklyHours.map(h => {
      if (h.day === day) {
        const updated = { ...h, [field]: value };
        // 시작시간을 입력했는데 끝나는시간이 비어있고 late도 아니면 자동으로 late 설정
        if (field === 'start' && value && !updated.end && !updated.isLate && !updated.closed) {
          updated.isLate = true;
        }
        // end 시간을 입력하면 late 해제
        if (field === 'end' && value) {
          updated.isLate = false;
        }
        return updated;
      }
      return h;
    });
    setWeeklyHours(updatedHours);
    
    // 압축 포맷도 업데이트
    if (operatingMode === 'detailed') {
      setCompactHours(formatWeeklyHoursToCompact(updatedHours));
    }
  };

  const handleClosedToggle = (day) => {
    const updatedHours = weeklyHours.map(h => 
      h.day === day ? { ...h, closed: !h.closed, start: !h.closed ? '' : h.start, end: !h.closed ? '' : h.end, isLate: false } : h
    );
    setWeeklyHours(updatedHours);
    
    // 압축 포맷도 업데이트
    if (operatingMode === 'detailed') {
      setCompactHours(formatWeeklyHoursToCompact(updatedHours));
    }
  };

  const handleLateToggle = (day) => {
    const updatedHours = weeklyHours.map(h => {
      if (h.day === day) {
        const updated = { ...h, isLate: !h.isLate };
        // late를 켜면 end 시간 클리어
        if (updated.isLate) {
          updated.end = '';
        }
        return updated;
      }
      return h;
    });
    setWeeklyHours(updatedHours);
    
    // 압축 포맷도 업데이트
    if (operatingMode === 'detailed') {
      setCompactHours(formatWeeklyHoursToCompact(updatedHours));
    }
  };

  // 새로운 운영시간 모드 관련 핸들러들
  const handleOperatingModeChange = (mode) => {
    setOperatingMode(mode);
    
    if (mode === 'compact') {
      // 상세 모드에서 압축 모드로 변경
      const newCompact = formatWeeklyHoursToCompact(weeklyHours);
      setCompactHours(newCompact);
    } else {
      // 압축 모드에서 상세 모드로 변경
      const newWeeklyHours = parseCompactToWeeklyHours(compactHours);
      setWeeklyHours(newWeeklyHours);
    }
  };

  const handleCompactHoursChange = (e) => {
    const value = e.target.value;
    setCompactHours(value);
    
    // 실시간으로 상세 모드 데이터도 업데이트
    const newWeeklyHours = parseCompactToWeeklyHours(value);
    setWeeklyHours(newWeeklyHours);
  };

  const validateCompactFormat = (compactString) => {
    if (!compactString.trim()) return { valid: true };
    
    // 다양한 포맷 검증 시도
    let scheduleJSON = null;
    let formatType = '';
    
    // 1. 압축 포맷 검증
    try {
      scheduleJSON = timeManager.parseCompactToJSON(compactString);
      if (scheduleJSON) formatType = '압축 포맷';
    } catch (e) {
      // 압축 포맷이 아님
    }
    
    // 2. 기존 상세 포맷 검증
    if (!scheduleJSON) {
      try {
        scheduleJSON = timeManager.parseStringToJSON(compactString);
        if (scheduleJSON) formatType = '상세 포맷';
      } catch (e) {
        // 상세 포맷도 아님
      }
    }
    
    if (!scheduleJSON) {
      return { 
        valid: false, 
        error: '지원되지 않는 형식입니다.\n• 한국어: "월/화/목/금/토/일 22:00-late"\n• 영어: "Everyday 21:00-late"\n• 특수시간: late, early, midnight, noon' 
      };
    }
    
    // 추가 검증
    const validation = timeManager.validateSchedule(scheduleJSON);
    if (!validation.valid) {
      return { valid: false, error: `${formatType} 검증 실패: ${validation.error}` };
    }
    
    return { valid: true, formatType };
  };

  const handleToggleSmoking = value => {
    setFormData(prev => ({ ...prev, smoking_policy: value }));
  };

  const handleToggleGenre = genre => {
    const current = formData.genres
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const updated = current.includes(genre)
      ? current.filter(g => g !== genre)
      : [...current, genre];
    setFormData(prev => ({ ...prev, genres: updated.join(',') }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    setNewImages(prev => [...prev, ...acceptedFiles]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] }
  });

  const handleRemoveExistingImage = url => {
    setExistingImages(prev => prev.filter(img => img.image_url !== url));
  };

  const handleRemoveNewImage = index => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // 제출 전 최종 검증
    const contactResult = containsContactInfo(formData.description);
    if (contactResult && Array.isArray(contactResult) && contactResult.length >= 2) {
      const [hasContactInfo, violations] = contactResult;
      if (hasContactInfo && violations && Array.isArray(violations)) {
        setContactError(`개인 연락처 정보는 입력할 수 없습니다. 감지된 항목: ${violations.join(', ')}`);
        window.scrollTo({ top: document.querySelector('textarea[name="description"]').offsetTop - 100, behavior: 'smooth' });
        return;
      }
    }
    
    // 압축 포맷 유효성 검사
    if (operatingMode === 'compact') {
      const validation = validateCompactFormat(compactHours);
      if (!validation.valid) {
        alert(`운영시간 형식 오류: ${validation.error}`);
        return;
      }
    }
    
    setIsSaving(true);
    const token = localStorage.getItem('ownerToken');
    const form = new FormData();

    // 기본 폼 필드 추가
    form.append('name', formData.name || '');
    form.append('korean_name', formData.korean_name || '');
    form.append('address', formData.address || '');
    form.append('description', formData.description || '');
    form.append('playlist', formData.playlist || '');
    form.append('genres', formData.genres || '');
    form.append('smoking_policy', formData.smoking_policy || '금연');

    // 운영 시간 추가 - 현재 모드에 따라 다른 포맷 사용
    let finalOperatingHours = '';
    if (operatingMode === 'compact') {
      finalOperatingHours = compactHours;
    } else {
      finalOperatingHours = formatOperatingHours(weeklyHours);
    }
    form.append('operating_hours', finalOperatingHours || '');

    // 가격 추가 - 값이 없거나 0이어도 명시적으로 전송
    const menPriceInt = priceMen === '' ? 0 : parseInt(priceMen);
    const womenPriceInt = priceWomen === '' ? 0 : parseInt(priceWomen);
    
    // 숫자를 명시적으로 전송 (NaN인 경우 0으로)
    form.append('price_men', isNaN(menPriceInt) ? 0 : menPriceInt);
    form.append('price_women', isNaN(womenPriceInt) ? 0 : womenPriceInt);

    // 기존 이미지 추가 (배열 확인)
    if (existingImages && Array.isArray(existingImages)) {
      existingImages.forEach(img => {
        form.append('existing_image_urls', img.image_url);
      });
    }
    
    // 새 이미지 추가 (배열 확인)
    if (newImages && Array.isArray(newImages)) {
      newImages.forEach(file => {
        form.append('new_images', file);
      });
    }

    try {
      const clubId = owner?.is_superuser ? selectedClubId : club?.id;
      if (!clubId) {
        setError('클럽 정보가 없습니다.');
        setIsSaving(false);
        return;
      }

      const res = await ownerAPI.updateClub(clubId, form);
      // ownerAPI가 직접 데이터를 반환하므로 res.data가 아닌 res 사용
      setExistingImages(res.images || []);
      setNewImages([]);
      setNewImagePreviews([]);
      setMainImage(res.images?.[0]?.image_url || '');
      
      // 응답받은 운영시간으로 UI 업데이트
      if(res.operating_hours) {
        populateFormData(res);
      }

      alert('클럽 정보가 성공적으로 수정되었습니다!');
      
      // 슈퍼유저이고 클럽 관리에서 온 경우 클럽 관리 페이지로 돌아가기
      if (owner?.is_superuser && searchParams.get('clubId')) {
        setTimeout(() => {
          navigate('/owner/clubs');
        }, 1000);
      }
    } catch (err) {
      console.error('서버 응답 오류:', err.response?.data || err.message);
      alert('수정 실패:\n' + (err.response?.data?.detail || err.message || '알 수 없는 오류'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const previewURLs = [
    mainImage,
    ...(existingImages || [])
      .map(img => img.image_url)
      .filter(url => url && url !== mainImage),
    ...(newImagePreviews || []).filter(url => url !== mainImage)
  ];

  // 미리보기용 운영시간 - 현재 모드에 따라 다른 포맷 사용
  let previewOperatingHours = '';
  if (operatingMode === 'compact') {
    previewOperatingHours = compactHours;
  } else {
    previewOperatingHours = formatOperatingHours(weeklyHours);
  }

  const previewClub = {
    name:        formData.name || '클럽명 없음',
    address:     formData.address,
    operating_hours: previewOperatingHours,
    price: `${!isNaN(parseInt(priceMen)) ? `남자: ${parseInt(priceMen).toLocaleString()}원` : ''}${!isNaN(parseInt(priceMen)) && !isNaN(parseInt(priceWomen)) ? ', ' : ''}${!isNaN(parseInt(priceWomen)) ? `여자: ${parseInt(priceWomen).toLocaleString()}원` : ''}` || '정보 없음',
    smoking_policy: formData.smoking_policy,
    instagram_url: club?.instagram_url || '',
    matchPercent: club?.matchPercent ?? 0,
    description: formData.description,
    genres:      formData.genres,
    images:      (previewURLs || []).map(src => ({ image_url: src }))
  };

  return (
    <div className="edit-wrapper">
      <OwnerSidebar />
      <main className="edit-main">
        <div className="edit-header">
        <h1>클럽 정보 수정</h1>
          {owner?.is_superuser && searchParams.get('clubId') && (
            <button 
              type="button" 
              className="back-btn"
              onClick={() => navigate('/owner/clubs')}
            >
              ← 클럽 관리로 돌아가기
            </button>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
        <div className="edit-grid">
          <form className="edit-form" onSubmit={handleSubmit}>
            {/* 슈퍼유저 클럽 선택 */}
            {owner?.is_superuser && (
              <>
                <label>클럽 선택</label>
                <select
                  value={selectedClubId || ''}
                  onChange={(e) => setSelectedClubId(parseInt(e.target.value))}
                  className="form-select"
                >
                  <option value="">클럽을 선택하세요</option>
                  {clubs.map((club) => (
                    <option key={club.id} value={club.id}>
                      {club.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label>클럽명</label>
            <input 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              className="form-input"
            />
            <label>한글명</label>
            <input 
              type="text" 
              name="korean_name" 
              value={formData.korean_name} 
              onChange={handleChange} 
              className="form-input"
            />
            <label>주소</label>
            <input 
              type="text" 
              name="address" 
              value={formData.address} 
              onChange={handleChange}
              className="form-input" 
            />
            <label>소개</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              rows="5"
              className="form-textarea" 
            />
            {contactError && <p className="error-message">{contactError}</p>}
            <p className="description-guide">* 개인 연락처 정보(전화번호, 카카오톡 ID, 이메일, 외부 웹사이트 등)는 입력할 수 없습니다.</p>
            <label>플레이리스트 URL</label>
            <input 
              type="text" 
              name="playlist" 
              value={formData.playlist} 
              onChange={handleChange}
              className="form-input" 
            />
            <div className="toggle-section">
              <h2>장르 선택</h2>
              <div className="toggle-group genres">
              {GENRE_LIST.map(genre => (
                <button
                  key={genre}
                  type="button"
                  className={formData.genres.split(',').map(s => s.trim()).includes(genre) ? 'toggle-btn active' : 'toggle-btn'}
                  onClick={() => handleToggleGenre(genre)}
                >
                  {genre}
                </button>
              ))}
               </div>
            </div>

            <label>운영 시간</label>
            
            {/* 운영시간 입력 모드 선택 */}
            <div className="operating-hours-mode-selection">
              <div className="mode-toggle-buttons">
                <button
                  type="button"
                  className={`mode-toggle-btn ${operatingMode === 'detailed' ? 'active' : ''}`}
                  onClick={() => handleOperatingModeChange('detailed')}
                >
                  상세 입력
                </button>
                <button
                  type="button"
                  className={`mode-toggle-btn ${operatingMode === 'compact' ? 'active' : ''}`}
                  onClick={() => handleOperatingModeChange('compact')}
                >
                  간편 입력
                </button>
              </div>
            </div>

            {operatingMode === 'compact' ? (
              /* 압축 포맷 입력 모드 */
              <div className="compact-hours-section">
                <p className="hours-instruction">
                  간편한 형식으로 운영시간을 입력하세요.<br/>
                  <strong>지원 포맷:</strong><br/>
                  • 한국어 압축: "목/금/토 22:00-04:00", "월/화/목/금/토/일 22:00-late"<br/>
                  • 특수 시간: "수/목/금 19:00-midnight", "토/일 21:00-early"<br/>
                  • 영어 포맷: "Everyday 21:00-late", "Mon/Tue/Wed 18:00-midnight"<br/>
                  • 복합 포맷: "수/목 19:00-00:00 (Bar 영업) 금/토 20:00-02:00"<br/>
                  <small>💡 특수 시간: late, early, midnight, noon 사용 가능</small>
                </p>
                <textarea
                  className="compact-hours-input"
                  value={compactHours}
                  onChange={handleCompactHoursChange}
                  placeholder="예: 목/금/토 22:00-04:00&#10;월/화/목/금/토/일 22:00-late&#10;수/목/금 19:00-midnight&#10;Everyday 21:00-late&#10;수/목 19:00-00:00 금/토 20:00-02:00"
                  rows={4}
                />
                
                {/* 실시간 검증 결과 표시 */}
                {compactHours && (() => {
                  const validation = validateCompactFormat(compactHours);
                  return (
                    <div className={`validation-feedback ${validation.valid ? 'valid' : 'invalid'}`}>
                      {validation.valid ? (
                        <span className="validation-success">
                          ✓ 올바른 형식입니다{validation.formatType ? ` (${validation.formatType})` : ''}
                        </span>
                      ) : (
                        <span className="validation-error">
                          ✗ {validation.error}
                        </span>
                      )}
                    </div>
                  );
                })()}
                
                <div className="compact-hours-preview">
                  <strong>미리보기:</strong>
                  <div className="preview-content">
                    {formatOperatingHours(weeklyHours) || '운영시간이 설정되지 않았습니다.'}
                  </div>
                </div>
              </div>
            ) : (
              /* 기존 상세 입력 모드 */
              <div className="detailed-hours-section">
                                  <p className="hours-instruction">
                    요일별 운영 시간을 설정하세요.<br/>
                    <small>💡 시작시간만 입력하면 자동으로 "late"(늦게까지)로 설정됩니다. "Late" 토글로 수동 조절 가능합니다.</small>
                  </p>
                <div className="operating-hours-section">
                  {weeklyHours.map((day) => (
                    <div key={day.day} className="day-row">
                      <span className="day-label">{day.label}</span>
                      <div className="time-inputs-wrapper">
                        <div className="time-input-pair">
                          <label htmlFor={`${day.day}-start`} className="time-input-label">시작</label>
                          <input
                            id={`${day.day}-start`}
                            type="time"
                            value={day.start}
                            onChange={(e) => handleWeeklyHoursChange(day.day, 'start', e.target.value)}
                            disabled={day.closed}
                            className="time-input-field"
                          />
                        </div>
                        <span className="time-separator">~</span>
                        <div className="time-input-pair">
                           <label htmlFor={`${day.day}-end`} className="time-input-label">종료</label>
                           <input
                             id={`${day.day}-end`}
                             type="time"
                             value={day.isLate ? '' : day.end}
                             onChange={(e) => handleWeeklyHoursChange(day.day, 'end', e.target.value)}
                             disabled={day.closed || day.isLate}
                             className="time-input-field"
                             placeholder={day.isLate ? 'late' : ''}
                           />
                           {day.isLate && (
                             <span className="late-indicator">late</span>
                           )}
                        </div>
                      </div>
                      <div className="toggle-controls">
                        <div className="closed-toggle">
                          <label htmlFor={`${day.day}-closed`} className="closed-label">휴무</label>
                          <Switch
                            id={`${day.day}-closed`}
                            checked={day.closed}
                            onChange={() => handleClosedToggle(day.day)}
                            className={`hours-switch ${day.closed ? 'closed' : 'open'}`}
                          >
                            <span className="switch-handle" />
                          </Switch>
                        </div>
                        <div className="late-toggle">
                          <label htmlFor={`${day.day}-late`} className="late-label">Late</label>
                          <Switch
                            id={`${day.day}-late`}
                            checked={day.isLate}
                            onChange={() => handleLateToggle(day.day)}
                            disabled={day.closed}
                            className={`hours-switch ${day.isLate ? 'late-active' : 'late-inactive'}`}
                          >
                            <span className="switch-handle" />
                          </Switch>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* 상세 모드에서 압축 포맷 미리보기 */}
                <div className="detailed-hours-preview">
                  <strong>압축 포맷 미리보기:</strong>
                  <div className="preview-content">
                    {compactHours || '압축 포맷으로 변환할 수 없습니다.'}
                  </div>
                </div>
              </div>
            )}

            <label>입장 가격</label>
            <div className="price-inputs-gender">
              <div className="price-input-group">
                 <label>남자</label>
                 <div className="price-input-wrapper">
                   <input
                     type="number"
                     value={priceMen}
                     onChange={(e) => setPriceMen(e.target.value.replace(/[^0-9]/g, ''))}
                     placeholder="가격 입력"
                     min="0"
                   />
                   <span className="unit">원</span>
                 </div>
              </div>
              <div className="price-input-group">
                 <label>여자</label>
                 <div className="price-input-wrapper">
                   <input
                     type="number"
                     value={priceWomen}
                     onChange={(e) => setPriceWomen(e.target.value.replace(/[^0-9]/g, ''))}
                     placeholder="가격 입력"
                     min="0"
                   />
                   <span className="unit">원</span>
                 </div>
              </div>
            </div>

            <div className="toggle-section">
            <h2>흡연 정책</h2>
            <div className="form-group">
              <select
                value={formData.smoking_policy}
                onChange={(e) => handleToggleSmoking(e.target.value)}
                className="form-select"
              >
                <option value="금연">금연</option>
                <option value="흡연">흡연</option>
                <option value="전자담배">전자담배</option>
              </select>
            </div>
          </div>

            <label>이미지 업로드</label>
            <div
              {...getRootProps()}
              className={`upload-area ${isDragActive ? 'drag-active' : ''}`}
            >
              <input {...getInputProps()} />
              {
                isDragActive
                  ? <p>이미지를 여기에 놓아주세요…</p>
                  : <p>이미지를 드래그하거나 클릭하여 업로드</p>
              }
            </div>
            <div className="image-preview-wrapper">
              <ReactSortable
                tag="div"
                className="sortable-list"
                list={(existingImages || []).map(img => ({ id: img.image_url, ...img }))}
                setList={sorted => setExistingImages(sorted)}
              >
                {(existingImages || []).map(imgObj => (
                  <div key={imgObj.image_url} className="image-thumb">
                    <img src={imgObj.image_url} alt="preview" />
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemoveExistingImage(imgObj.image_url)}
                    >&times;</button>
                  </div>
                ))}
              </ReactSortable>

              <div className="new-list"></div>
                 {(newImagePreviews || []).map((src, idx) => (
                    <div key={src} className="image-thumb">
                    <img src={src} alt="preview" />
                    <button
                        type="button"
                        className="remove-button"
                        onClick={() => handleRemoveNewImage(idx)}
                    >&times;</button>
                    </div>
              ))}
            </div>

        <button type="submit" disabled={isSaving} className="submit-button">
          {isSaving ? '저장 중…' : '수정 완료'}
        </button>
          </form>

        <div className="edit-preview">
        <h2>미리보기</h2>
            <div className="preview-layout">
              <div className="preview-block">
                <h3>Card Preview</h3>
                <ClubCard
                  club={{
                    id: club?.id ?? 0,
                    name: formData.name,
                    genres: formData.genres,
                    like_count: club?.like_count ?? 0,
                    matchPercent: club?.matchPercent ?? 0
                  }}
                  mainImage={mainImage}
                  isSelected={false}
                  showLikeButton={false}
                  onCardClick={() => {}}
                  onLikeClick={() => {}}
                />
              </div>
              <div className="preview-block">
                <h3>Popup Preview</h3>
                <div className="popup-preview-container">
                    <span className="popup-back-symbol-club">&#x2304;</span>
                    <div className="club-detail-header">
                    <h1 className="club-detail-title">
                        {previewClub.name}
                    </h1>
                    {previewClub.instagram_url && (
                        <button className="club-detail-instagram-button">
                        <i className="fa-brands fa-instagram" />
                        </button>
                    )}
                    </div>
                    <div className="info-section">
                    <h2 className="section-title">About</h2>
                    <div className="info-box">
                        <p className="club-detail-address">
                        <i className="fa-solid fa-map-marker-alt icon" />{' '}
                        {previewClub.address || '정보 없음'}
                        </p>
                        <p className="club-detail-time" style={{ whiteSpace: 'pre-line' }}>
                          <i className="fa-solid fa-clock icon" />{' '}
                          {previewClub.operating_hours || '정보 없음'}
                        </p>
                        <p className="club-detail-entry">
                          <i className="fa-solid fa-user icon" /> 입장료{' '}
                          {previewClub.price || '정보 없음'}
                        </p>
                        <p className="club-detail-smoking">
                          <i className="fa-solid fa-smoking-ban icon" />{' '}
                          {previewClub.smoking_policy}
                        </p>
                        <p className="club-detail-description">
                          <i className="fa-solid fa-info-circle icon" />{' '}
                          {previewClub.description?.split('\n').map((line, i) => (
                            <span key={i}>{line}<br/></span>
                          )) || '소개 없음'}
                        </p>
                        <p className="club-detail-match">
                          플레이리스트 매치:
                          <strong> {previewClub.matchPercent}%</strong>
                        </p>
                    </div>
                    </div>
                    <div className="genre-section">
                      <h2 className="section-title">Genre</h2>
                      <div className="genre-box-container">
                        {previewClub.genres
                          ? previewClub.genres.split(',').map((g,i) => (
                              <span key={i} className="genre-box">{g.trim()}</span>
                            ))
                          : <span className="genre-box">정보 없음</span>}
                      </div>
                    </div>
                    <div className="image-section">
                      <div className="club-detail-images-multi">
                        <div className="multi-image-container">
                          {(previewClub.images || []).map((img, idx) => (
                            <div key={idx} className="image-wrapper">
                              <img
                                src={img.image_url}
                                alt={`Preview ${idx}`}
                                className="multi-image"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                </div>
              </div>
            </div>
        </div>
        </div>
      </main>
    </div>
  );
}
